import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import {
  WEBHOOK_EVENT_STATUSES,
  type WebhookEvent,
  type WebhookEventStatus,
} from "@/contracts";

import { webhookEventsQuery, type WebhookEventFilters } from "@/api/queries";
import { useApiClient } from "@/lib/api-context";
import { useAuth } from "@/auth/auth-context";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { useListFilters } from "@/lib/use-list-filters";
import { formatDateTime, formatDevEui, formatRelative } from "@/lib/format";

/**
 * Dead letters and other webhook outcomes.
 *
 * Tenant admins see events matched to their devices; unmatched uplinks stay
 * on the super-admin view (ADR-087).
 */
export function WebhookEventsPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const client = useApiClient();
  const { value: filterValue, page, setFilter, setPage } = useListFilters();

  const status = (filterValue("status") as WebhookEventStatus | "") || "DEAD_LETTER";

  const filters: WebhookEventFilters = {
    page,
    status,
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
  };

  const events = useQuery(webhookEventsQuery(client, filters));

  return (
    <>
      <PageHeader title={t("webhookEventsTitle")} description={t("webhookEventsDescription")} />

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <SearchInput
              value={filters.search ?? ""}
              placeholder={t("searchWebhookEvents")}
              onChange={(value) => setFilter("search", value)}
            />
            <SelectField
              label={t("filterWebhookStatus")}
              value={status}
              onChange={(value) => setFilter("status", value === "DEAD_LETTER" ? "" : value)}
              data={WEBHOOK_EVENT_STATUSES.map((entry) => ({
                value: entry,
                label: t(`webhookStatus.${entry}`),
              }))}
            />
          </div>
        </CardBody>
      </Card>

      {events.isPending ? <LoadingBlock label={t("loadingWebhookEvents")} /> : null}

      {events.isError ? (
        <ErrorBlock error={events.error} onRetry={() => void events.refetch()} />
      ) : null}

      {events.data ? (
        <Card>
          <CardBody className="px-0 py-0">
            <DataTable caption={t("webhookEventsTitle")}>
              <thead>
                <tr>
                  <Th>{t("colReceived")}</Th>
                  <Th>{t("colStatus")}</Th>
                  {isSuperAdmin ? <Th>{t("colOrganization")}</Th> : null}
                  <Th>{t("colDevice")}</Th>
                  <Th>{t("colError")}</Th>
                  <Th>{t("colProviderEvent")}</Th>
                </tr>
              </thead>
              <tbody>
                {events.data.data.length === 0 ? (
                  <EmptyRow colSpan={isSuperAdmin ? 6 : 5}>{t("emptyWebhookEvents")}</EmptyRow>
                ) : (
                  events.data.data.map((entry) => (
                    <WebhookEventRow
                      key={entry.id}
                      entry={entry}
                      showOrganization={isSuperAdmin}
                    />
                  ))
                )}
                <tr>
                  <Td colSpan={isSuperAdmin ? 6 : 5}>
                    <Pagination
                      page={events.data.pagination.page}
                      totalPages={events.data.pagination.totalPages}
                      total={events.data.pagination.total}
                      noun={{
                        one: tCommon("noun.entry.one"),
                        other: tCommon("noun.entry.other"),
                      }}
                      onChange={setPage}
                    />
                  </Td>
                </tr>
              </tbody>
            </DataTable>
          </CardBody>
        </Card>
      ) : null}
    </>
  );
}

function WebhookEventRow({
  entry,
  showOrganization,
}: {
  entry: WebhookEvent;
  showOrganization: boolean;
}) {
  const { t } = useTranslation("pages");

  return (
    <tr>
      <Td>
        <span title={formatDateTime(entry.receivedAt)}>{formatRelative(entry.receivedAt)}</span>
      </Td>
      <Td>{t(`webhookStatus.${entry.status}`)}</Td>
      {showOrganization ? <Td>{entry.organizationName ?? t("noOrganization")}</Td> : null}
      <Td>
        {entry.deviceId ? (
          <Link
            to={`/devices/${entry.deviceId}`}
            className="font-medium text-brand-strong underline underline-offset-2"
          >
            {entry.deviceName ?? (entry.devEui ? formatDevEui(entry.devEui) : entry.deviceId)}
          </Link>
        ) : (
          <span className="text-ink-muted">{t("unmatchedDevice")}</span>
        )}
      </Td>
      <Td>
        <span className="text-sm">{entry.error ?? "—"}</span>
      </Td>
      <Td>
        <span className="font-mono text-xs">{entry.providerEventId}</span>
      </Td>
    </tr>
  );
}
